import { ChangeEvent, KeyboardEvent, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../app/hooks";
import { Material } from "../../features/materials/models/Material";
import { useAttachmentUpload } from "../../features/messages/hooks/useAttachmentUpload";
import { MessagesChannel } from "../../features/messages/models/MessagesChannel";
import { attachMaterial } from "../../features/messages/slice-extensions/attachments-slice-extension";
import { MAX_ATTACHMENTS } from "../../features/messages/utils/attachmentRules";
import { isTouchDevice, shouldSendOnEnter } from "../../features/messages/utils/composerKeys";
import AttachMenu from "./AttachMenu";
import LibraryPicker from "./LibraryPicker";
import PendingAttachments from "./PendingAttachments";

/** Six lines of `text-base` (24 px line height); beyond that the textarea scrolls. */
const MAX_HEIGHT_PX = 144;
const MAX_LENGTH = 4000;

interface MessageComposerProps {
  channel: MessagesChannel;
  threadId: string;
  /** Resolves true when the message went out, so the draft and its attachments can be cleared. */
  onSend: (body: string, attachmentIds: string[]) => Promise<boolean>;
  busy: boolean;
}

/**
 * Multi-line composer with attachments. On a keyboard Enter sends and Shift+Enter adds a line; on a
 * touch device Enter adds a line and the Send button sends. Grows to six lines, then scrolls. A
 * message may be only attachments; it waits until every upload has completed.
 */
const MessageComposer = ({ channel, threadId, onSend, busy }: MessageComposerProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [draft, setDraft] = useState("");
  const [libraryOpen, setLibraryOpen] = useState(false);
  const uploads = useAttachmentUpload(channel, threadId);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const touch = isTouchDevice();
  const hasContent = draft.trim().length > 0 || uploads.attachmentIds.length > 0;
  const canSend = hasContent && !uploads.uploading && !busy;
  const full = uploads.items.length >= MAX_ATTACHMENTS;

  useLayoutEffect(() => {
    const element = textarea.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${Math.min(element.scrollHeight, MAX_HEIGHT_PX)}px`;
  }, [draft]);

  const send = async () => {
    if (!canSend) return;
    if (await onSend(draft, uploads.attachmentIds)) {
      setDraft("");
      uploads.clear();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    const keyEvent = { key: event.key, shiftKey: event.shiftKey, isComposing: event.nativeEvent.isComposing };
    if (shouldSendOnEnter(keyEvent, touch)) {
      event.preventDefault();
      void send();
    }
  };

  const addFiles = (files: File[]) =>
    files.slice(0, MAX_ATTACHMENTS - uploads.items.length).forEach((file) => void uploads.upload(file));

  const pickMaterial = async (material: Material) => {
    setLibraryOpen(false);
    try {
      uploads.addCompleted(await dispatch(attachMaterial({ threadId, materialId: material.id })).unwrap());
    } catch {
      toast.error(t("messages.attach.failed"));
    }
  };

  return (
    <div className="border-t border-grey-90 pt-3 dark:border-primary-20">
      <PendingAttachments items={uploads.items} onRemove={uploads.remove} />
      <div className="flex items-end gap-2">
        <div className="relative flex-1">
          <textarea
            ref={textarea}
            rows={1}
            maxLength={MAX_LENGTH}
            value={draft}
            aria-label={t("messages.composer.placeholder")}
            placeholder={t("messages.composer.placeholder")}
            className="input min-h-0 w-full resize-none overflow-y-auto pe-11 leading-6"
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setDraft(event.target.value)}
            onKeyDown={handleKeyDown}
          />
          <div className="absolute bottom-1.5 end-1.5">
            <AttachMenu
              onFiles={addFiles}
              onLibrary={channel.kind === "staff" ? () => setLibraryOpen(true) : undefined}
              disabled={full}
            />
          </div>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => void send()} disabled={!canSend}>
          {t("messages.composer.send")}
        </button>
      </div>
      {!touch && (
        <p className="mt-1 text-xs text-grey-40 dark:text-grey-80">{t("messages.composer.hint")}</p>
      )}
      {libraryOpen && <LibraryPicker onPick={(m) => void pickMaterial(m)} onClose={() => setLibraryOpen(false)} />}
    </div>
  );
};

export default MessageComposer;
